import React, { useEffect, useState } from "react";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { api, errMsg, fmtDate, fmtINR } from "../lib/api";
import { pokeLiveCollections } from "../lib/liveCollections";

export const MIN_REASON = 5;
export const RESTORE_WINDOW_DAYS = 30;

/**
 * Phase 177 — "Delete this receipt?" confirmation with the MANDATORY reason.
 * `receipt` = {id, receipt_number|receipt_display, amount, payment_date, pin, owner_name, collected_by_name, municipality?}
 * `code`    = tenant code the receipt belongs to (the Super Admin page passes the row's municipality code).
 */
export const DeleteReceiptDialog = ({ open, onOpenChange, receipt, code, onDeleted, testId = "delete-receipt" }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setReason(""); setBusy(false); } }, [open]);
  const trimmed = reason.trim();
  const valid = trimmed.length >= MIN_REASON;
  const rn = receipt?.receipt_display || receipt?.receipt_number || "";

  const confirm = async () => {
    if (!valid || !receipt) return;
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/collections/receipts/${receipt.id}/delete`, { reason: trimmed });
      toast.success(`Receipt ${r.data.receipt_number} deleted`, {
        description: `Hidden from every list and total. It can be restored from the Deleted receipts tab until ${fmtDate(r.data.restore_until)}.`,
      });
      pokeLiveCollections();
      onOpenChange(false);
      onDeleted?.(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="sm:max-w-lg" data-testid={`${testId}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Trash2 className="h-5 w-5 text-destructive" /> Delete receipt {rn}?
          </DialogTitle>
          <DialogDescription>
            The receipt is removed from every register, report, dashboard and the collector's totals immediately, and its money goes
            back on the property's outstanding demand. It stays in <span className="font-medium">Deleted receipts</span> for {RESTORE_WINDOW_DAYS} days,
            where it can be restored. After {RESTORE_WINDOW_DAYS} days only the Super Admin can remove it permanently.
          </DialogDescription>
        </DialogHeader>
        {receipt && (
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm grid grid-cols-2 gap-x-4 gap-y-1" data-testid={`${testId}-summary`}>
            <span className="text-muted-foreground">Receipt</span><span className="font-mono text-right">{rn}</span>
            <span className="text-muted-foreground">Amount</span><span className="tabular-nums font-medium text-right" data-testid={`${testId}-amount`}>{fmtINR(receipt.amount)}</span>
            <span className="text-muted-foreground">Date</span><span className="text-right tabular-nums">{fmtDate(receipt.payment_date)}</span>
            <span className="text-muted-foreground">Property</span><span className="text-right font-mono">{receipt.pin || "—"}</span>
            {receipt.owner_name && (<><span className="text-muted-foreground">Owner</span><span className="text-right truncate">{receipt.owner_name}</span></>)}
            {receipt.collected_by_name && (<><span className="text-muted-foreground">Collected by</span><span className="text-right truncate">{receipt.collected_by_name}</span></>)}
            {receipt.municipality?.name && (<><span className="text-muted-foreground">Municipality</span><span className="text-right truncate">{receipt.municipality.name}</span></>)}
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor={`${testId}-reason`}>Reason for deletion <span className="text-destructive">*</span></Label>
          <Textarea id={`${testId}-reason`} value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500}
            placeholder="e.g. Entered against the wrong PIN; duplicate of receipt RCP-…; amount keyed incorrectly"
            aria-invalid={trimmed.length > 0 && !valid} data-testid={`${testId}-reason-input`} />
          <p className={`text-[11px] ${trimmed.length > 0 && !valid ? "text-destructive" : "text-muted-foreground"}`} data-testid={`${testId}-reason-hint`}>
            Mandatory — at least {MIN_REASON} characters. Your name, the time and this reason are logged with the receipt and in the Audit Trail.
          </p>
        </div>
        <div className="flex items-start gap-2 rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-3 py-2 text-xs text-foreground" data-testid={`${testId}-warning`}>
          <AlertTriangle className="h-4 w-4 shrink-0 text-[hsl(var(--warning))] mt-0.5" />
          <span>If this receipt belongs to a cheque, the cheque disappears from Cheque Deposits as well; everything comes back on restore.</span>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid={`${testId}-cancel`}>Cancel</Button>
          <Button variant="destructive" onClick={confirm} disabled={!valid || busy} data-testid={`${testId}-confirm`}>
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />} {busy ? "Deleting…" : "Delete receipt"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteReceiptDialog;
